import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Message, MessageService } from 'primeng/api';
import { AuthService } from 'src/app/service/auth.service';
import { CustomValidator } from 'src/shared/custom-validator';
import { CognitoException } from 'src/app/model/enum/cognito-exception';
import { AccountService } from 'src/app/service/account.service';
import { Language } from 'src/app/model/language';
import { LANGUAGE_LIST } from 'src/app/model/mockup/language-mockup';
import { GeolocationService } from 'src/app/service/geolocation.service';
import { SessionPersistenceService } from 'src/app/service/session-persistence.service';
import { LoginResponse, challengeOutcome } from 'src/app/model/response/auth-response';
import { AccountDeletionService, PENDING_DELETION_ROUTE } from 'src/app/service/account-deletion.service';
import { isSessionStoreUnavailable } from 'src/app/model/session-store-unavailable';

@Component({
  selector: 'app-mfa-login',
  templateUrl: './mfa-login.component.html',
  styleUrl: './mfa-login.component.css'
})
export class MfaLoginComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
public form!: FormGroup;

  session: string | null = null;
  userName: string | null = null;
  code: string = '';
  email: string | null = null;
  messages: Message[];

  isDivCreate: string;
  isDivActivated: string;
  isDivExpired: string;
  loginUrl: string;
  dashboardUrl: string;
  messagesCode: Message[] = [];

  isLoadingBtn: boolean = false

  languageOptions: Language[];
  DEFAULT_LANGUAGE: Language;
  lastLanguage: Language;
  language: Language;

  constructor(private formBuilder: FormBuilder, 
    private router: Router,
    private titleService: Title,
    private translocoService: TranslocoService,
    private authService: AuthService,
    private messageService: MessageService,
    private accountService: AccountService,
    private geolocationService: GeolocationService,
    private sessionPersistence: SessionPersistenceService,
    private accountDeletion: AccountDeletionService) {

    this.isDivCreate='';
    this.isDivActivated='none';
    this.isDivExpired='none';

    this.loginUrl = '/login';
    this.dashboardUrl = '/my-garage';
    this.messages = [];

    this.languageOptions = LANGUAGE_LIST as [];
    this.DEFAULT_LANGUAGE = this.languageOptions[0];
    this.language = this.DEFAULT_LANGUAGE;
    this.lastLanguage = this.DEFAULT_LANGUAGE;
  }

  ngOnInit(): void {
    let lan = sessionStorage.getItem('language');
    this.createForm(this.code);
    this.changeLanguage(this.getLanguage(lan).name);
    this.refreshLanguageOptions();
    this.getLocation();
    this.setTitle();
    this.session= sessionStorage.getItem('session');
    this.email = sessionStorage.getItem('email');
  }

  createForm(code: string) {
    this.form = this.formBuilder.group({
      code: [code, CustomValidator.getRequired()]
    })
  }

  async onSubmit() {
    if (this.session !== null && this.email !== null) {
      try {
        this.isLoadingBtn = true
        await this.authService.respondToChallenge(this.session, this.email, this.f.code.value)
          .then(result => {
            // Código errado com tentativa sobrando chega como 200, não como erro — tem que
            // ser testado antes do caminho de sucesso.
            if (challengeOutcome(result) === 'retry') {
              this.isLoadingBtn = false;
              this.handleChallengeRetry(result);
              return;
            }
            if (result) {
              const token = sessionStorage.getItem('token');
              // Conta em carência de exclusão: o /user/active daria 403. Não consome o
              // postLoginRedirect (serve se a pessoa desistir).
              if (token && result.authenticationResult && this.accountDeletion.isPending) {
                this.isLoadingBtn = false;
                this.sessionPersistence.persist();
                this.router.navigate([PENDING_DELETION_ROUTE]);
                return;
              }
              if (token) {
                this.accountService.findUserActive().subscribe(
                  data => {
                    if (data) {
                      // sessionStorage.setItem('idUser', data.userID);
                      sessionStorage.setItem('nameUser', data.name);
                      sessionStorage.setItem('language', data.language);
                      if (data.accessLevel < 1) {
                        this.messageService.clear();
                        this.translocoService.selectTranslate('user.login.accessDenied').pipe(take(1)).subscribe(value => {
                          this.messageService.add({
                            key: 'mfalogin',
                            severity: 'error',
                            summary: value,
                          });
                        });
                      } else {
                        this.isLoadingBtn = false

                        this.sessionPersistence.persist();
                        const redirect = this.authService.consumePostLoginRedirect();
                        if (redirect) {
                          this.router.navigateByUrl(redirect);
                          return;
                        }
                        this.router.navigate([this.dashboardUrl]);
                      }
                    } else {
                      this.isLoadingBtn = false
                      this.handleLoginError('user.login.errorMfa');
                    }
                  },
                  error => {
                    this.isLoadingBtn = false
                    // 503 no /user/active (erro cru, sem mapApiError): não é código errado.
                    this.handleLoginError(isSessionStoreUnavailable(error) ? 'user.login.serviceUnavailable' : 'user.login.errorMfa');
                  }
                );
              } else {
                this.isLoadingBtn = false
                this.resetCodeAndShowError('user.login.errorMfa');
              }
            } else {
              this.isLoadingBtn = false
              this.resetCodeAndShowError('user.login.errorMfa');
            }
          })
          .catch(error => {
            this.isLoadingBtn = false
            this.handleErrorResponse(error);
          });
      } catch (error) {
        this.isLoadingBtn = false
        this.handleErrorResponse(error); 
      }
    }
  }
  
  /**
   * Código errado, ainda há tentativa. Não é erro HTTP nem pedido de código novo: o código
   * do e-mail continua valendo e nenhum e-mail novo foi enviado.
   *
   * O que muda é a sessão. Ela é de uso único — cada resposta traz uma nova, e reenviar a
   * anterior devolve "Invalid session for the user". Não sobrescrever aqui era o que fazia
   * a segunda tentativa morrer mesmo com o código certo.
   */
  private handleChallengeRetry(result: LoginResponse) {
    if (result.session) {
      this.session = result.session;
      // Espelha no sessionStorage: é de lá que o ngOnInit relê a sessão se a página recarregar.
      sessionStorage.setItem('session', result.session);
    }
    this.f.code.setValue('');
    this.messageService.clear();

    // attemptsRemaining pode vir null (o Cognito nem sempre informa); nesse caso o aviso
    // vai sem número, em vez de dizer "restam null tentativas".
    const remaining = result.attemptsRemaining;
    const key = remaining != null
      ? 'user.confirmLoginMfa.codeIncorrectWithAttempts'
      : 'user.confirmLoginMfa.codeIncorrect';

    this.translocoService.selectTranslate(key, { count: remaining }).subscribe(value => {
      this.messageService.add({
        key: 'mfalogin',
        severity: 'error',
        summary: value,
      });
    }).unsubscribe();
  }

  private resetCodeAndShowError(translationKey: string) {
    this.f.code.setValue('');
    this.messageService.clear();
    this.translocoService.selectTranslate(translationKey).pipe(take(1)).subscribe(value => {
      this.messageService.add({
        key: 'mfalogin',
        severity: 'error',
        summary: value,
      });
    });
  }
  

  /**
   * Erros que significam sessão do desafio inutilizável. Desde a retentativa de OTP,
   * código errado não é mais erro HTTP — vem 200 com `challengeRetry`. Então um erro de
   * autenticação aqui só sobra para tentativas esgotadas ou sessão morta, e nos dois casos
   * o formulário na tela não tem mais como funcionar: o caminho é refazer o login, que
   * gera um código novo.
   */
  private static readonly DEAD_SESSION_ERRORS: string[] = [
    CognitoException.NOT_AUTHORIZED,
    CognitoException.CODE_MISMATCH,
    CognitoException.EXPIRED_CODE,
  ];

  private handleErrorResponse(error: any) {
    let messageError = '';
    this.f.code.setValue('');
    this.messageService.clear();
    const cognitoError = error as { name: string };
    // Redis de sessões fora do ar: indisponibilidade temporária, NÃO é sessão morta — sem
    // endDeadSession, o usuário fica na tela e repete o código quando o serviço voltar.
    if (isSessionStoreUnavailable(error)) {
      this.translocoService.selectTranslate('user.login.serviceUnavailable').pipe(take(1)).subscribe(value => {
        this.messageService.add({
          key: 'mfalogin',
          severity: 'error',
          summary: value,
        });
      });
      return;
    }

    // Falha de rede não mata a sessão: repetir é legítimo, então continua na tela.
    if (MfaLoginComponent.DEAD_SESSION_ERRORS.includes(cognitoError?.name)) {
      this.endDeadSession();
      return;
    }

    this.translocoService.selectTranslateObject('cognito.insuccess').subscribe(value => {
      if (CognitoException.USER_NOT_CONFIRMED === cognitoError.name)
        messageError = value.UserNotConfirmedException;
      else if (CognitoException.NETWORKING_ERROR === cognitoError.name)
        messageError = value.NetworkingError;
      else 
        messageError = value.errorMfa;
  
      this.messageService.add({
        key: 'mfalogin',
        severity: 'error',
        summary: messageError,
      });
    }).unsubscribe();
  }

  /**
   * Sessão do desafio acabou: volta pro login, que é onde um código novo é gerado.
   *
   * O aviso vai por query param em vez de toast porque o toast desta tela (key
   * "mfalogin") morre junto com o componente na navegação — o usuário seria jogado no
   * login sem explicação nenhuma. A tela de login já sabe exibir motivo vindo da URL; é
   * o mesmo caminho que o interceptor usa no 401 SESSION_EXPIRED.
   */
  private endDeadSession() {
    // Deixar a sessão morta gravada faria o ngOnInit de uma volta futura a esta tela
    // reidratar um desafio que não vale mais.
    sessionStorage.removeItem('session');
    this.router.navigate([this.loginUrl], { queryParams: { mfa_error: 'session_ended' } });
  }
  

  private handleLoginError(translationKey: string) {
    this.messageService.clear();
    this.translocoService.selectTranslate(translationKey).pipe(take(1)).subscribe(value => {
      this.messageService.add({
        key: 'mfalogin',
        severity: 'error',
        summary: value,
      });
    });
  }
  
  
  sendEmailAgain() {
    if(this.session !== null && this.email !== null)
    this.authService.resendConfirmationCode(this.email).then(() => {
    }).catch(() => {
      this.messageService.clear();
      this.translocoService.selectTranslate('errorConection').pipe(take(1)).subscribe(value => 
        this.messageService.add({
          key: 'mfalogin',
          severity: 'error',
          summary: value,
        })
      );
    });
  }

  redirect() {
    this.router.navigate([this.loginUrl]);
  }

  expired() {
    this.router.navigate([this.loginUrl]);
  }

  showCreate() {
    this.isDivCreate = "";
    this.isDivActivated = "none";
    this.isDivExpired="none";
  }

  showActivated() {
    this.isDivCreate = "none";
    this.isDivActivated = "";
    this.isDivExpired="none";
    setTimeout(() => {
      this.router.navigate([this.loginUrl]);
    }, 5000);  //5s
  }

  showExpired() {
    this.isDivCreate = "none";
    this.isDivActivated = "none";
    this.isDivExpired="";
  }
  
  setTitle() {
    this.translocoService.selectTranslate('title.activate-account').pipe(takeUntil(this.destroy$)).subscribe(value => 
      this.titleService.setTitle(value)
    );
  }


  fixLanguage(event:any) {
    this.language = event.option;    
    if(this.lastLanguage !== this.language) {
        this.languageOptions
          .filter(opt => opt.name === this.language.name)
          .map(cty => cty.disabled = true)
        this.languageOptions
          .filter(opt => opt.name !== this.language.name)
          .map(cty => cty.disabled = false)

        this.lastLanguage = this.language;
        this.changeLanguage(this.language.name);
        sessionStorage.setItem('language', this.language.name);
    }
  }

  getLanguage(lan: string | null) : Language {
    for (let i = 0; i < this.languageOptions.length; i++) {
      if(this.languageOptions[i].name === lan) {
        return this.languageOptions[i];
      }
    }
    return this.DEFAULT_LANGUAGE;
  }

  public changeLanguage(languageCode: string | null): void {
    if(languageCode !== null)
      this.translocoService.setActiveLang(languageCode);
  }

  getLocation(){
    this.geolocationService.getCountry().subscribe((data: any) => {
      if (data.country_code === "BR") {
        this.language = this.languageOptions[0];
      } else {
        this.language = this.languageOptions[1];
      }
    });
  }

  refreshLanguageOptions() {
    for (let i = 0; i < this.languageOptions.length; i++) {
      if(this.languageOptions[i].name === this.language.name)
        this.languageOptions[i].disabled = true;
      else
       this.languageOptions[i].disabled = false;
    }
  }


  get asideKey(): string {
    return this.isDivExpired === '' ? 'user.authAside.mfa.expired' : 'user.authAside.mfa.code';
  }

  get f() { return this.form.controls; }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
